import type { DeepPartial, Styles } from 'klinecharts'

/**
 * Chart color settings whose default comes from the APP THEME.
 *
 * `lineColor` is deliberately absent — line/area charts keep klinecharts' blue
 * until the user picks something, and no theme token drives it.
 */
export const THEMED_COLOR_KEYS = [
  'candleUpColor',
  'candleDownColor',
  'gridHorizontalColor',
  'gridVerticalColor',
  'crosshairColor',
  'crosshairLabelBg',
  'separatorColor',
] as const

/**
 * Border/wick color settings. Their default is the THEME's own border/wick
 * value; the effective candle body is only a FALLBACK for themes that don't
 * define one. They are deliberately NOT coupled to a customized body —
 * changing the body color must change the body alone (recoloring borders and
 * wicks as a side effect read as a bug, and resetting the body visually reset
 * all three elements).
 */
export const BODY_DERIVED_COLOR_KEYS = [
  'candleBorderUpColor',
  'candleBorderDownColor',
  'wickUpColor',
  'wickDownColor',
] as const

/**
 * Session overlay colors whose default comes from the APP THEME (per profile),
 * gated the same way as the themed keys above: a stored value only wins once the
 * user has explicitly picked it (it lands in `customizedColors`). The theme
 * defaults are painted to <canvas>, so the host resolves them to concrete color
 * strings and publishes them through ChartThemeColorsContext — they are NOT read
 * out of the klinecharts style bundle by `themedColorsFrom`.
 */
export const SESSION_COLOR_KEYS = [
  'preMarketColor',
  'afterHoursColor',
  'sessionBreakColor',
] as const

/** Every color setting that has a default worth falling back to. */
export const CUSTOMIZABLE_COLOR_KEYS = [
  ...THEMED_COLOR_KEYS,
  ...BODY_DERIVED_COLOR_KEYS,
  ...SESSION_COLOR_KEYS,
] as const

export type ThemedColorKey = (typeof CUSTOMIZABLE_COLOR_KEYS)[number]

export type ThemedColors = Partial<Record<ThemedColorKey, string>>

const isCustomizableKey = (key: string): key is ThemedColorKey =>
  (CUSTOMIZABLE_COLOR_KEYS as readonly string[]).includes(key)

/** True when `key` names a color setting that has a default to fall back to. */
export function isThemedColorSetting(key: string): boolean {
  return isCustomizableKey(key)
}

/**
 * Pull the theme's value for each themed key out of a klinecharts style bundle
 * (the output of `buildChartTheme()` / `applyTheme()`), border/wick included —
 * each element defaults to its OWN theme value, independent of the body.
 */
export function themedColorsFrom(theme: DeepPartial<Styles> | undefined): ThemedColors {
  if (!theme || typeof theme !== 'object') return {}
  const t = theme as DeepPartial<Styles>
  const out: ThemedColors = {}
  const put = (key: ThemedColorKey, val: unknown) => {
    if (typeof val === 'string' && val !== '') out[key] = val
  }
  put('candleUpColor', t.candle?.bar?.upColor)
  put('candleDownColor', t.candle?.bar?.downColor)
  put('candleBorderUpColor', t.candle?.bar?.upBorderColor)
  put('candleBorderDownColor', t.candle?.bar?.downBorderColor)
  put('wickUpColor', t.candle?.bar?.upWickColor)
  put('wickDownColor', t.candle?.bar?.downWickColor)
  put('gridHorizontalColor', t.grid?.horizontal?.color)
  put('gridVerticalColor', t.grid?.vertical?.color)
  put('crosshairColor', t.crosshair?.horizontal?.line?.color)
  put('crosshairLabelBg', t.crosshair?.horizontal?.text?.backgroundColor)
  put('separatorColor', t.separator?.color)
  return out
}

/**
 * Resolve what a color setting should actually be.
 *
 * The rule is explicit, not inferred: a key uses its default UNLESS it appears
 * in `customizedColors` — the list the settings modal appends to when the user
 * picks a color by hand.
 *
 * This replaces an older heuristic ("stored value differs from the factory
 * default ⇒ the user must have customized it"), which guessed wrong in both
 * directions. Any stray stored value — an earlier theme's colors, a loaded
 * settings profile — read as "customized" and locked the theme out of that
 * panel permanently; and a user who deliberately picked the factory default had
 * their choice silently overwritten. Neither was recoverable per-field.
 *
 * Migration is free: settings saved before `customizedColors` existed have no
 * entries, so they fall back to the theme — the state users expect after
 * picking one.
 */
export function effectiveColor(
  key: ThemedColorKey,
  stored: string,
  defaults: ThemedColors,
  customizedColors: readonly string[] | undefined,
): string {
  if (customizedColors?.includes(key)) return stored
  return defaults[key] || stored
}

interface CandleBodySettings {
  candleUpColor: string
  candleDownColor: string
  customizedColors?: string[]
}

/**
 * Fill in border/wick defaults ONLY where the theme left them empty, from the
 * THEME's body color (never the user's customized body — element independence:
 * editing or resetting the body must touch the body alone).
 *
 * Idempotent, and never overwrites present entries — safe to run over a map
 * that already carries border/wick values.
 */
export function withBodyDerived(
  defaults: ThemedColors,
  _settings: CandleBodySettings,
): ThemedColors {
  const upBody = defaults.candleUpColor
  const downBody = defaults.candleDownColor
  return {
    candleBorderUpColor: upBody,
    candleBorderDownColor: downBody,
    wickUpColor: upBody,
    wickDownColor: downBody,
    ...Object.fromEntries(Object.entries(defaults).filter(([, v]) => v)),
  }
}

/**
 * The full default map for one chart: theme values for the themed keys, plus
 * the effective candle body for the border/wick keys.
 *
 * `useChartStyles` (what gets painted) and the settings modal (what the swatch
 * shows) both resolve through this, so the panel can never display a color that
 * isn't on the canvas.
 */
export function chartColorDefaults(
  theme: DeepPartial<Styles> | undefined,
  settings: CandleBodySettings,
): ThemedColors {
  return withBodyDerived(themedColorsFrom(theme), settings)
}

/**
 * Add a key to the customized list (idempotent, order-stable). Keys with no
 * default are ignored — only the ones that would otherwise be overridden need
 * tracking.
 */
export function markColorCustomized(
  customizedColors: readonly string[] | undefined,
  key: string,
): string[] {
  const list = customizedColors ?? []
  if (!isCustomizableKey(key) || list.includes(key)) return [...list]
  return [...list, key]
}

/** Drop a key from the customized list — "go back to the default". */
export function clearColorCustomized(
  customizedColors: readonly string[] | undefined,
  key: string,
): string[] {
  return (customizedColors ?? []).filter((k) => k !== key)
}

/**
 * The customized-colors set after the user picks `val` for `key`.
 *
 * Picking the theme's own color RE-ATTACHES the key (clears the flag) instead
 * of pinning an identical color — otherwise choosing the swatch that equals the
 * theme raised a reset arrow though nothing visibly changed. Any other color
 * pins it. Non-customizable keys pass the list through untouched.
 */
export function customizedColorsAfterPick(
  customizedColors: readonly string[] | undefined,
  key: string,
  val: string,
  themeValue: string | undefined,
): string[] {
  if (!isCustomizableKey(key)) return [...(customizedColors ?? [])]
  const followsTheme = !!themeValue && val.trim().toLowerCase() === themeValue.trim().toLowerCase()
  return followsTheme
    ? clearColorCustomized(customizedColors, key)
    : markColorCustomized(customizedColors, key)
}
